import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Category colour palette (subtle, coherent with dark theme accent gold #BCAC8A)
const CATEGORY_COLORS = {
  ristoranti: '#e07a5f',
  divertimento: '#d64545',
  negozi: '#9c6ade',
  'shopping-center': '#7b5eb7',
  sport: '#43a047',
  natura: '#2e7d32',
  cultura: '#3a86ff',
  relax: '#ec4899',
  emergenze: '#ef4444',
  supermercati: '#8bc34a',
  minimarket: '#8bc34a',
  noleggi: '#0ea5e9',
  trasporti: '#0891b2',
  lavanderie: '#64748b',
  tour: '#f59e0b',
  bancomat: '#78716c',
  poste: '#eab308',
  tabacchini: '#a16207',
  partner: '#BCAC8A',
  _me: '#ff5252',
};

const iconCache = new Map();

function categoryIcon(category, isUser = false, sponsored = false) {
  const key = isUser ? '_me' : (sponsored ? `_spon_${category}` : (category || 'partner'));
  if (iconCache.has(key)) return iconCache.get(key);
  const color = isUser ? CATEGORY_COLORS._me : (sponsored ? '#BCAC8A' : (CATEGORY_COLORS[category] || '#BCAC8A'));
  const [w, h] = sponsored ? [42, 58] : [30, 42];
  const svg = sponsored ? `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 42 58">
      <defs>
        <linearGradient id="spon-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#f2e5b8"/>
          <stop offset="50%" stop-color="#BCAC8A"/>
          <stop offset="100%" stop-color="#8f7f5f"/>
        </linearGradient>
      </defs>
      <path fill="url(#spon-grad)" stroke="#0a0a0a" stroke-width="2"
        d="M21 1 C10 1 3 8 3 19 c0 13 18 37 18 37 s18-24 18-37 c0-11-7-18-18-18 z"/>
      <circle cx="21" cy="19" r="8.5" fill="#0a0a0a"/>
      <path fill="#BCAC8A" transform="translate(14.5,12.5)" d="M6.5 0.5 l1.8 3.8 4.2 0.6 -3 3 0.7 4.2 -3.7 -2 -3.7 2 0.7 -4.2 -3 -3 4.2 -0.6 z"/>
    </svg>` : `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 30 42">
      <path fill="${color}" stroke="#111" stroke-width="1.5"
        d="M15 1 C7 1 2 6 2 14 c0 9 13 26 13 26 s13-17 13-26 c0-8-5-13-13-13 z"/>
      <circle cx="15" cy="14" r="5" fill="#fff" />
      ${isUser ? '<circle cx="15" cy="14" r="2.5" fill="#ff5252" />' : ''}
    </svg>`;
  const icon = new L.Icon({
    iconUrl: 'data:image/svg+xml;utf8,' + encodeURIComponent(svg),
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    popupAnchor: [0, -(h - 8)],
    className: sponsored ? 'yup-sponsored-marker' : ''
  });
  iconCache.set(key, icon);
  return icon;
}

function createClusterCustomIcon(cluster) {
  const count = cluster.getChildCount();
  const size = count < 10 ? 34 : count < 50 ? 42 : 52;
  return L.divIcon({
    html: `<div style="
        background: linear-gradient(135deg, #BCAC8A, #8f7f5f);
        color: #0a0a0a;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        font-weight: 700;
        font-size: ${size > 40 ? 14 : 12}px;
        border: 3px solid #fff;
        box-shadow: 0 2px 8px rgba(0,0,0,0.5);
      ">${count}</div>`,
    className: 'yup-cluster-icon',
    iconSize: L.point(size, size, true),
  });
}

const LEGEND_ITEMS = [
  ['ristoranti', 'Ristoranti'],
  ['divertimento', 'Divertimento'],
  ['sport', 'Sport'],
  ['cultura', 'Cultura'],
  ['natura', 'Natura'],
  ['relax', 'Relax'],
  ['negozi', 'Negozi'],
  ['emergenze', 'Emergenze'],
  ['noleggi', 'Noleggi'],
  ['trasporti', 'Trasporti'],
];

/**
 * Props:
 *  - activities: [{id, name, latitude, longitude, address, category_key, ...}]
 *  - fallbackCenter: [lat, lng] (default: Desenzano)
 *  - height: string (default: '360px')
 *  - showLegend: bool (default true)
 */
export default function ActivityMap({
  activities = [],
  fallbackCenter = [45.4694, 10.5417],
  height = '360px',
  showLegend = true,
}) {
  const geoActivities = useMemo(
    () => activities.filter(a => typeof a.latitude === 'number' && typeof a.longitude === 'number'),
    [activities]
  );

  const center = useMemo(() => {
    if (geoActivities.length === 0) return fallbackCenter;
    const lat = geoActivities.reduce((s, a) => s + a.latitude, 0) / geoActivities.length;
    const lng = geoActivities.reduce((s, a) => s + a.longitude, 0) / geoActivities.length;
    return [lat, lng];
  }, [geoActivities, fallbackCenter]);

  // Categories actually present in this map (for the legend)
  const presentCats = useMemo(
    () => new Set(geoActivities.map(a => a.id === '_me' ? '_me' : a.category_key)),
    [geoActivities]
  );

  if (geoActivities.length === 0) {
    return (
      <div style={{ height }}
           className="rounded-lg border border-[color:var(--yup-border)] bg-black/20 flex items-center justify-center text-white/50 text-sm"
           data-testid="map-empty">
        Nessuna attività geo-localizzata da mostrare in mappa
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden border border-[color:var(--yup-border)]"
         style={{ height }} data-testid="activity-map">
      <MapContainer
        center={center}
        zoom={geoActivities.length === 1 ? 15 : 12}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MarkerClusterGroup
          iconCreateFunction={createClusterCustomIcon}
          showCoverageOnHover={false}
          maxClusterRadius={50}
        >
          {geoActivities.map(a => {
            const sponsored = a.id !== '_me' && a.sponsor_active && a.sponsor_active !== 'none';
            return (
              <Marker
                key={a.id}
                position={[a.latitude, a.longitude]}
                icon={categoryIcon(a.category_key, a.id === '_me', sponsored)}
                zIndexOffset={sponsored ? 1000 : 0}
              >
                <Popup>
                  <div className="text-sm min-w-[200px]">
                    <div className="flex items-center gap-1.5">
                      <div className="font-semibold text-gray-900">{a.name}</div>
                      {sponsored && (
                        <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{background:'linear-gradient(135deg,#f2e5b8,#BCAC8A)',color:'#0a0a0a'}}>Sponsor</span>
                      )}
                    </div>
                    {a.category_key && a.id !== '_me' && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {a.category_key}{a.subcategory ? ` · ${a.subcategory}` : ''}
                      </div>
                    )}
                    {a.address && <div className="text-gray-600 text-xs mt-1">{a.address}</div>}
                    {a.phone && (
                      <a href={`tel:${a.phone}`} className="text-blue-600 text-xs block mt-1">
                        📞 {a.phone}
                      </a>
                    )}
                    {a.website && (
                      <a href={a.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs block mt-1">
                        🌐 Sito
                      </a>
                    )}
                    {typeof a.distance_km === 'number' && (
                      <div className="text-xs text-gray-500 mt-1">🧭 {a.distance_km} km da te</div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>

      {showLegend && (
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-md shadow px-3 py-2 text-xs text-gray-800 max-w-[240px] z-[1000]"
             data-testid="map-legend">
          <div className="font-semibold mb-1 text-gray-900">Categorie</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {LEGEND_ITEMS.filter(([k]) => presentCats.has(k)).map(([k, label]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-full border border-black/40"
                      style={{ background: CATEGORY_COLORS[k] || '#BCAC8A' }} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
